/**
 * Shared primitives for branded error pages (#2454).
 *
 * Rules:
 *  - ZERO dependency on ConfigProvider / AuthProvider / BrandProvider / ThemeProvider.
 *    These pages must render when the backend is completely down.
 *  - NO hardcoded hex colour literals. Use var(--brand-*) tokens only.
 *    brand.css is injected by the Vite entry point (index.css → brand.css),
 *    which is a plain <link> tag — no JS fetch required.
 *  - Mobile-first responsive layout.
 */

import type { ReactNode, CSSProperties } from "react";

// ── CSS injected via <style> so no separate stylesheet fetch is required ──────
export const PAGE_CSS = `
  *,*::before,*::after { box-sizing: border-box; }

  .err-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: var(--brand-bg-default);
    padding: 1.5rem;
    font-family: var(--brand-font-sans);
  }

  .err-card {
    background: var(--brand-bg-elevated);
    border-radius: 0.875rem;
    padding: 2.5rem 2rem;
    max-width: 480px;
    width: 100%;
    text-align: center;
    box-shadow: var(--brand-shadow-card);
  }

  .err-logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 2rem;
    text-decoration: none;
  }

  .err-logo-hex {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }

  .err-logo-wordmark {
    font-size: 1.25rem;
    font-weight: 700;
    background: var(--brand-gradient-gold);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.02em;
  }

  .err-code {
    font-size: 4rem;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 0.5rem;
    letter-spacing: -0.04em;
    opacity: 0.9;
  }

  .err-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--brand-text-primary);
    margin: 0 0 0.75rem;
  }

  .err-message {
    font-size: 0.875rem;
    color: var(--brand-text-secondary);
    margin: 0 0 2rem;
    line-height: 1.6;
  }

  .err-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    align-items: center;
  }

  .err-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.6rem 1.5rem;
    border-radius: 0.5rem;
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    border: none;
    background: var(--brand-gradient-gold);
    color: var(--brand-text-inverse);
    transition: opacity 0.15s ease, transform 0.15s ease;
    min-width: 200px;
    justify-content: center;
  }
  .err-btn-primary:hover {
    opacity: 0.88;
    transform: translateY(-1px);
  }
  .err-btn-primary:active {
    transform: translateY(0);
  }

  .err-btn-secondary {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.55rem 1.5rem;
    border-radius: 0.5rem;
    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    background: transparent;
    border: 1px solid var(--brand-border-strong);
    color: var(--brand-text-secondary);
    transition: border-color 0.15s ease, color 0.15s ease;
    min-width: 200px;
    justify-content: center;
  }
  .err-btn-secondary:hover {
    border-color: var(--brand-border-focus);
    color: var(--brand-text-primary);
  }

  @media (min-width: 480px) {
    .err-actions {
      flex-direction: row;
      justify-content: center;
    }
    .err-btn-primary,
    .err-btn-secondary {
      min-width: auto;
    }
  }
`;

// SVG <defs> linearGradient stopColor cannot use CSS vars — paint server resolves before cascade.
const LOGO_GOLD_START = "#f7dc6f"; // brand-exception: SVG stopColor; mirrors --brand-accent; CBO: brand-officer-agent 2026-06-01
const LOGO_GOLD_END   = "#f5a623"; // brand-exception: SVG stopColor; mirrors --brand-accent-secondary; CBO: brand-officer-agent 2026-06-01

// ── Brand logo (inline SVG honeycomb hex + wordmark) ─────────────────────────
export function Logo() {
  return (
    <a href="/" className="err-logo" aria-label="2hives.ai — go to homepage">
      {/* Minimal honeycomb hexagon */}
      <svg
        className="err-logo-hex"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <polygon
          points="16,2 28,9 28,23 16,30 4,23 4,9"
          fill="none"
          stroke="url(#hexGrad)"
          strokeWidth="2.2"
          strokeLinejoin="round"
        />
        {/* Inner honeycomb cell */}
        <polygon
          points="16,8 22,11.5 22,18.5 16,22 10,18.5 10,11.5"
          fill="url(#hexGrad)"
          opacity="0.7"
        />
        <defs>
          <linearGradient id="hexGrad" x1="4" y1="2" x2="28" y2="30" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor={LOGO_GOLD_START} />
            <stop offset="100%" stopColor={LOGO_GOLD_END} />
          </linearGradient>
        </defs>
      </svg>
      <span className="err-logo-wordmark">2hives.ai</span>
    </a>
  );
}

// ── Error card ────────────────────────────────────────────────────────────────

interface ErrorCardProps {
  code: string;
  title: string;
  message: string;
  /** CSS variable name for the accent colour, e.g. "--brand-status-error" */
  accentVar: string;
  /** Inline background for the code badge */
  accentBg: string;
  /** Border colour string for the card edge */
  accentBorder: string;
  /** Show a "Retry" button that calls window.location.reload() */
  showRetry?: boolean;
  children?: ReactNode;
}

export function ErrorCard({
  code,
  title,
  message,
  accentVar,
  accentBg,
  accentBorder,
  showRetry = false,
  children,
}: ErrorCardProps) {
  const cardStyle: CSSProperties = {
    border: `1px solid ${accentBorder}`,
  };
  const codeStyle: CSSProperties = {
    color: `var(${accentVar})`,
    background: accentBg,
    display: "inline-block",
    padding: "0.15rem 0.75rem",
    borderRadius: "0.5rem",
    marginBottom: "1.25rem",
  };

  return (
    <div className="err-card" style={cardStyle}>
      {children}

      <div className="err-code" style={codeStyle}>
        {code}
      </div>

      <h1 className="err-title">{title}</h1>
      <p className="err-message">{message}</p>

      <div className="err-actions">
        <a href="/hives" className="err-btn-primary">
          Back to dashboard
        </a>
        {showRetry && (
          <button
            className="err-btn-secondary"
            onClick={() => window.location.reload()}
            type="button"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
